import {
  InvestmentFormFields,
  InvestmentFormFieldsOptional,
  useInvestmentAddForm,
} from "features/investments/InvestmentForm";
import { InvestmentEntityReadData } from "gen/model/investmentEntityReadData";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const AddInvestmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "investment"]);

  const { fundFamilyId } = useRequiredStringParams();

  const onCancel = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .investments({}).$,
    );
  }, [navigate, fundFamilyId]);

  const onSuccess = useCallback(
    (data: InvestmentEntityReadData) => {
      navigate(
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .investments({})
          .investment({ investmentId: data.id }).$,
      );
    },
    [navigate, fundFamilyId],
  );

  const formProps = useInvestmentAddForm({
    fundFamilyId,
  });

  return (
    <PageContent contentWidth="boxed">
      <PageHeader
        title={t("investment:add.pageTitle")}
        workflowName={t("investment:add.pageSuperTitle")}
        size="lg"
      />
      <PageBody>
        <Form {...formProps} onSuccess={onSuccess}>
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockBody variant="form">
                <InvestmentFormFields fundFamilyId={fundFamilyId} />
              </ContentBlockBody>
            </ContentBlock>
            <ContentBlock>
              <ContentBlockHeader>
                <ContentBlockTitle>
                  {t("common:optional", {
                    field_name: t("common:additionalInformation"),
                  })}
                </ContentBlockTitle>
              </ContentBlockHeader>
              <ContentBlockBody variant="form">
                <InvestmentFormFieldsOptional />
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
          <FormFooter>
            <FormUnsavedIndicator />
            <FormFooterActions>
              <FormCancelButton onClick={onCancel} />
              <FormSubmitButton>{t("investment:add.button")}</FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
